"use client"

import React, { useState, useEffect, useMemo } from "react"
import { MessageCircle, User } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { ScrollArea } from "./ui/scroll-area"
import { Avatar, AvatarFallback } from "./ui/avatar"
import { useSession } from "../context/SessionContext"
import { useTranslation } from "react-i18next"
import AssignmentChatDialog from "./AssignmentChatDialog"

interface ChatNotification {
  roomId: string
  assignedBy: string
  assignerName: string
  transferId: number
  lastMessage: string
  timestamp: Date
  unreadCount: number
}

const ChatNotifications: React.FC = () => {
  const { t } = useTranslation()
  const { session, allAssistants } = useSession()
  const [isOpen, setIsOpen] = useState(false)
  const [chatNotifications, setChatNotifications] = useState<ChatNotification[]>([])
  const [chatDialog, setChatDialog] = useState<{
    isOpen: boolean
    assignedBy: string
    assignerName: string
    transferId: number
  }>({
    isOpen: false,
    assignedBy: '',
    assignerName: '',
    transferId: 0
  })

  useEffect(() => {
    if (!session?.user?.id) return
    
    const serverIp = process.env.REACT_APP_LIVE_CHAT_SERVER
    const wsUrl = `wss://${serverIp}/ws/notifications/${session.user.id}`
    
    const ws = new WebSocket(wsUrl)
    
    ws.onopen = () => {}

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        
        if (data.type === 'chat_notification') {
          
          const senderAgent = allAssistants.find(
            (assistant: any) => assistant.id === data.assignedBy
          )
          const senderName = senderAgent?.name || t("unknown_user")
          
          setChatNotifications(prev => {
            const existingIndex = prev.findIndex(n => n.roomId === data.roomId)
            
            if (existingIndex >= 0) {
              const updated = [...prev]
              updated[existingIndex] = {
                ...updated[existingIndex],
                lastMessage: data.lastMessage,
                timestamp: new Date(data.timestamp),
                unreadCount: updated[existingIndex].unreadCount + 1
              }
              return updated
            } else {
              return [...prev, {
                roomId: data.roomId,
                assignedBy: data.assignedBy,
                assignerName: senderName,
                transferId: data.transferId,
                lastMessage: data.lastMessage,
                timestamp: new Date(data.timestamp),
                unreadCount: 1
              }]
            }
          })
        }

        if (data.type === 'chat_read') {
          setChatNotifications(prev => 
            prev.filter(n => n.roomId !== data.roomId)
          )
        }
      } catch (error) {
        console.error('Error parsing chat notification:', error)
      }
    }

    ws.onerror = (error) => {
      console.error('❌ [Frontend] Chat notifications WebSocket error:', error)
    }

    ws.onclose = (event) => {
      console.log('🔴 [Frontend] Chat notifications WebSocket disconnected')
    }

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, [session?.user?.id, allAssistants])

  const totalUnreadChats = useMemo(() => {
    return chatNotifications.reduce((total, notification) => total + notification.unreadCount, 0)
  }, [chatNotifications])

  const handleChatClick = (notification: ChatNotification) => {
    setChatDialog({
      isOpen: true,
      assignedBy: notification.assignedBy,
      assignerName: notification.assignerName,
      transferId: notification.transferId
    })
    setIsOpen(false)
    
    setChatNotifications(prev => 
      prev.filter(n => n.roomId !== notification.roomId)
    )
  }

  const closeChatDialog = () => {
    setChatDialog({
      isOpen: false,
      assignedBy: '',
      assignerName: '',
      transferId: 0
    })
  }

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <MessageCircle className="h-5 w-5" />
            {totalUnreadChats > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              >
                {totalUnreadChats > 99 ? "99+" : totalUnreadChats}
              </Badge>
            )}
            <span className="sr-only">{t("chat_notifications")}</span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-80 p-0">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-sm">{t("pending_chats")}</h3>
            <p className="text-xs text-muted-foreground">
              {totalUnreadChats > 0 ? (
                <>
                  {totalUnreadChats} {t("unread_messages")}
                </>
              ) : (
                t("no_pending_chats")
              )}
            </p>
          </div>

          <ScrollArea className="max-h-96">
            {chatNotifications.length > 0 ? (
              <div className="divide-y">
                {chatNotifications.map((notification) => (
                  <div
                    key={notification.roomId}
                    className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => handleChatClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-green-500 text-white text-sm">
                          {notification.assignerName
                            .split(" ")
                            .map((n: string) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-foreground">
                            {notification.assignerName}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              {notification.timestamp.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                            {notification.unreadCount > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {notification.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground truncate mt-1">
                          {notification.lastMessage}
                        </p>
                        <div className="flex items-center gap-1 mt-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          <span className="text-xs text-green-600 font-medium">
                            {t("new_message")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <User className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">
                  {t("no_active_chats")}
                </p>
              </div>
            )}
          </ScrollArea>
        </DropdownMenuContent>
      </DropdownMenu>

      <AssignmentChatDialog
        isOpen={chatDialog.isOpen}
        onClose={closeChatDialog}
        assignedBy={chatDialog.assignedBy}
        assignerName={chatDialog.assignerName}
        transferId={chatDialog.transferId}
      />
    </>
  )
}

export default ChatNotifications